import React from 'react';

import SEO from './SEO';

const ANIMATIONS_DESCRIPTION =
  'A gallery of interactive web animation experiments by Sander Kuyken — 3D galleries, scroll-triggered effects, page transitions and creative text reveals built with GSAP and Three.js.';

const AnimationGallery = () => {
  const animations = [
    {
      id: 'ashfall-3d-gallery',
      name: 'Ashfall 3D Image Gallery',
      description: 'Interactive 3D image gallery with smooth transitions and depth effects',
      category: 'Gallery',
      dependencies: ['GSAP', 'Three.js'],
      path: 'cg-ashfall-3d-image-gallery/cg-ashfall-v1'
    },
    {
      id: 'scroll-animation',
      name: 'Heurebleue Scroll Animation',
      description: 'Smooth scroll-triggered animations with parallax effects',
      category: 'Scroll',
      dependencies: ['GSAP', 'ScrollTrigger'],
      path: 'cg-heurebleue-scroll-animation'
    },
    {
      id: 'playable-objects',
      name: 'Playable Objects',
      description: 'Interactive 3D objects with physics and user interaction',
      category: 'Interactive',
      dependencies: ['Three.js', 'Cannon.js'],
      path: 'cg-playable-objects'
    },
    {
      id: 'yzavoku-slider',
      name: 'Yzavoku Slider',
      description: 'Advanced image slider with smooth transitions and effects',
      category: 'Slider',
      dependencies: ['GSAP'],
      path: 'cg-yzavoku-slider'
    },
    {
      id: 'team-section',
      name: 'Interactive Team Section',
      description: 'Dynamic team member cards with hover effects and animations',
      category: 'Cards',
      dependencies: ['GSAP', 'Framer Motion'],
      path: 'codegrid-interactive-team-section-javascript'
    },
    {
      id: 'page-transition',
      name: 'Madeinux Studio Page Transition',
      description: 'Smooth page transitions with morphing effects',
      category: 'Navigation',
      dependencies: ['Next.js', 'Framer Motion'],
      path: 'codegrid-madeinuxstudio-page-transition-nextjs'
    },
    {
      id: 'sticky-cards',
      name: 'Sticky Cards',
      description: 'Sticky scrolling cards with smooth animations',
      category: 'Cards',
      dependencies: ['Next.js', 'GSAP'],
      path: 'codegrid-sticky-cards-nextjs'
    },
    {
      id: 'telescope-text',
      name: 'Telescope Text Animation',
      description: 'Creative text reveal animation with telescope effect',
      category: 'Text',
      dependencies: ['GSAP'],
      path: 'codegrid-telescope-text-animation'
    },
    {
      id: 'terminal-text',
      name: 'Terminal Text Reveal',
      description: 'Terminal-style text typing animation with code effects',
      category: 'Text',
      dependencies: ['Next.js', 'GSAP'],
      path: 'codegrid-terminal-text-reveal-animation'
    },
    {
      id: 'hover-effect',
      name: 'Wildyriftian Hover Effect',
      description: 'Advanced hover effects with image morphing and transitions',
      category: 'Hover',
      dependencies: ['GSAP'],
      path: 'codegrid-wildyriftian-hover-effect'
    }
  ];

  const categories = [...new Set(animations.map(anim => anim.category))];

  return (
    <div className="min-h-screen bg-black text-white py-20">
      <SEO
        title="Animation Gallery"
        description={ANIMATIONS_DESCRIPTION}
        path="/animations"
      />
      <div className="max-w-7xl mx-auto px-5">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="font-anton text-6xl md:text-8xl text-white mb-6">
            ANIMATION
          </h1>
          <h2 className="font-anton text-4xl md:text-6xl text-white mb-8">
            GALLERY
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Browse through our collection of clean, modern animations. 
            Click on any animation to see it in action and decide which ones to integrate.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button className="px-6 py-3 bg-white text-black rounded-full font-semibold hover:bg-gray-200 transition-colors">
            All
          </button>
          {categories.map(category => (
            <button 
              key={category}
              className="px-6 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
            >
              {category}
            </button>
          ))}
        </div>

        {/* Animation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {animations.map((animation) => (
            <div 
              key={animation.id}
              className="bg-gray-900 rounded-2xl overflow-hidden hover:scale-105 transition-transform duration-300 group"
            >
              {/* Preview Image */}
              <div className="h-48 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-2">🎬</div>
                  <p className="text-sm opacity-80">Preview</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1 bg-blue-500 text-white text-xs rounded-full">
                    {animation.category}
                  </span>
                  <div className="flex gap-1">
                    {animation.dependencies.map((dep, index) => (
                      <span 
                        key={index}
                        className="w-2 h-2 bg-green-400 rounded-full"
                        title={dep}
                      ></span>
                    ))}
                  </div>
                </div>

                <h3 className="font-bold text-xl mb-2 group-hover:text-blue-400 transition-colors">
                  {animation.name}
                </h3>
                
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {animation.description}
                </p>

                {/* Dependencies */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Dependencies:</p>
                  <div className="flex flex-wrap gap-1">
                    {animation.dependencies.map((dep, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded"
                      >
                        {dep}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <a 
                    href={`/animations/${animation.path}/index.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex-1 text-center py-2 px-4 rounded-lg font-semibold transition-colors ${
                      animation.dependencies.includes('Next.js') 
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {animation.dependencies.includes('Next.js') ? 'View (Next.js)' : 'Open Animation'}
                  </a>
                  <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    Integrate
                  </button>
                </div>
                
                {/* Next.js Warning */}
                {animation.dependencies.includes('Next.js') && (
                  <div className="mt-2 p-2 bg-yellow-900/30 border border-yellow-600/50 rounded text-xs text-yellow-300">
                    ⚠️ This animation requires Next.js and may not work in React
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Info */}
        <div className="text-center mt-16 text-gray-400">
          <p className="mb-2">
            All animations are clean, modern, and ready for integration
          </p>
          <p className="text-sm">
            Click "View Demo" to see each animation in action
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnimationGallery;
